import { PropsWithChildren, ReactNode } from 'react'
import { useRouter } from 'next/router'
import { twJoin } from 'tailwind-merge'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import useUser from 'shared/hooks/use-user'
import { isCustomDomain } from 'shared/utils/is-custom-domain'
import { useBrand } from 'modules/white-label/contexts/brand-context'
import { useAvailableSections } from '../hooks/use-available-sections'
import { getDashboardLink, getShowDashboardLink } from '../menu/utils'
import type { AvailableSectionsInterface } from '../types/available-sections-interface'

interface LogoLinkProps extends PropsWithChildren {
  logoSrc?: string
}

const getLogoUrl = (
  route: string,
  availableSections?: AvailableSectionsInterface,
): '/community' | '/school' | undefined => {
  if (availableSections?.communities && availableSections.courses) {
    return route.includes('school') ? '/school' : '/community'
  }

  if (availableSections === undefined) return undefined

  if (
    availableSections.courses ||
    (availableSections.communities === undefined && availableSections.courses === undefined)
  )
    return '/school'

  if (availableSections.communities) return '/community'
}

const LogoLink = ({ children, logoSrc }: LogoLinkProps): ReactNode => {
  const { user } = useUser()
  const router = useRouter()
  const { data: availableSections } = useAvailableSections()
  const logoUrl = getLogoUrl(router.route, availableSections)
  const { brand } = useBrand()
  const shouldRenderDashboardLink = getShowDashboardLink(user)
  const dashboardLink = getDashboardLink(user)
  const shouldRenderBasic = shouldRenderDashboardLink && !isCustomDomain()

  if (shouldRenderBasic) {
    return (
      <a
        className={twJoin(!shouldRenderDashboardLink && 'pointer-events-none')}
        href={dashboardLink}
      >
        {children}
      </a>
    )
  }

  if (logoUrl === undefined) {
    return <img src={logoSrc || brand.logo?.path} alt={brand.name} className="max-h-8" />
  }

  return <LinkWithoutPrefetch href={logoUrl}>{children}</LinkWithoutPrefetch>
}

export default LogoLink
